import type { OpeningHours } from 'lib-shared-types-api';
import moment from 'moment-timezone';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { flowsApi } from 'apis/flows';
import { AnnouncementItem } from 'components/shared/announcementPicker/adapters/announcementAdapter';
import { useEditorContext } from 'flow-builder/editor.context';
import type { Relation } from 'flow-builder/types/relation';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { getExceptionsFromHolidays } from 'utils/holidays';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { getUserDisplayName, useCurrentUser } from 'utils/user';

import { useMigrateOpeningHoursContext } from '../migrateOpeningHours.context';
import type { BaseProps } from '../types/props';
import { createBusinessHoursNode } from '../utils/createBusinessHoursNode';

export function useMigrateSchedule(props: BaseProps): [VoidFunction, boolean] {
  const { node, replaceNode, triggerClose } = props;
  const { schedule, announcement, holidays, name, timezone } =
    useMigrateOpeningHoursContext();
  const {
    state: { setSelectState },
  } = useEditorContext();
  const notify = useNotifications();
  const [createSchedule, { isLoading }] =
    flowsApi.useCreateFlowScheduleMutation();
  const dispatch = useAppDispatch();
  const { user } = useCurrentUser();
  const agentName = getUserDisplayName(user);

  const handleCreateSchedule = useCallback(async () => {
    try {
      const holidaysResponse = holidays?.countryCode
        ? await dispatch(
            flowsApi.endpoints.readFlowsSchedulesHolidays.initiate({
              country_code: holidays.countryCode,
              timezone,
            }),
          )
        : null;

      const exceptions = holidaysResponse
        ? [
            ...getExceptionsFromHolidays({
              agentName,
              holidays: holidaysResponse.data?.data.holidays,
              isClosed: !!holidays?.isClosedOnHolidays,
            }),
          ]
        : [];

      const openingHours = schedule.map<OpeningHours>(({ date, intervals }) => {
        const localDate = moment.utc();
        const parsedDate = moment.utc(date);
        const dayOfWeek = parsedDate.day() || 7;

        const today = moment.utc().isoWeekday();
        const momentWeekday = moment.utc().isoWeekday(dayOfWeek);

        if (today >= dayOfWeek) {
          momentWeekday.add(1, 'weeks');
        }

        return {
          created_at: localDate.toISOString(),
          created_by: agentName,
          duration: 1,
          id: uuid(),
          intervals,
          name: parsedDate.format('dddd'),
          opening_hour_type: 'regular',
          recurring: {
            day_of_week: dayOfWeek,
          },
          start_date: momentWeekday.toISOString(),
        };
      });

      const res = await createSchedule({
        announcement: announcement as AnnouncementItem,
        created_at: moment.utc().toISOString(),
        country_code: holidays?.countryCode ?? null,
        name,
        timezone,
        opening_hours: [...openingHours, ...exceptions],
      });

      const isSuccess = 'data' in res;

      if (!isSuccess) {
        notify(
          {
            title: 'An error occurred when trying to create schedule.',
          },
          NotificationType.Error,
        );

        return;
      }

      const newNode = createBusinessHoursNode(node, res.data.id);

      replaceNode(newNode);
      triggerClose();
      setSelectState({ node: newNode as Relation });
    } catch (error) {
      console.warn(error);

      notify(
        {
          title: 'An error occurred when trying to create schedule.',
        },
        NotificationType.Error,
      );
    }
  }, [
    agentName,
    announcement,
    holidays,
    name,
    node,
    timezone,
    schedule,
    createSchedule,
    dispatch,
    notify,
    replaceNode,
    setSelectState,
    triggerClose,
  ]);

  return [handleCreateSchedule, isLoading];
}
